// src/pages/Register.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';
import { motion } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google'; // Provider va en App.jsx
import './Register.css';
import illustration from '../assets/login-illustration.jpeg';

export default function Register() {
  const [name, setName]               = useState('');
  const [lastName, setLastName]       = useState('');
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd]         = useState(false);
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get('redirectTo') || '/bienvenida'; // o '/plans'

  // ✅ Si ya hay token, redirige automáticamente (no pasar por login)
  useEffect(() => {
    const token = localStorage.getItem('api_token');
    if (token) {
      navigate(redirectTo, { replace: true });
    }
  }, [navigate, redirectTo]);

  const submit = async e => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post('/register', {
        name,
        last_name: lastName,
        email,
        password,
        password_confirmation: confirmPassword, // Laravel lo necesita
        role: 'alumno',
      });

      // ✅ Auto-login
      localStorage.setItem('api_token', data.token);
      // El interceptor de api.js ya leerá el token en próximas requests

      navigate('/bienvenida', { state: { name }, replace: true }); // o navigate(redirectTo, { replace: true })
    } catch (err) {
      console.error(err);
      const serverMessage = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat().join(' ')
        : err.response?.data?.message || 'Error al crear cuenta';
      setError(serverMessage);
    } finally {
      setLoading(false);
    }
  };

  // === Google Login (usa el provider global de App.jsx) ===
  const onGoogleSuccess = async (credentialResponse) => {
    setError('');
    try {
      setGoogleLoading(true);
      const credential = credentialResponse?.credential;
      if (!credential) {
        setError('No se recibió el token de Google.');
        return;
      }

      const { data } = await api.post('/oauth/google', { credential });

      // ✅ Auto-login con token de tu API
      localStorage.setItem('api_token', data.token);

      const displayName = data?.user?.name || 'Estudiante';
      navigate('/bienvenida', { state: { name: displayName }, replace: true }); // o navigate(redirectTo, { replace: true })
    } catch (err) {
      console.error(err);
      const serverMessage = err.response?.data?.message || 'No se pudo iniciar sesión con Google';
      setError(serverMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const onGoogleError = () => {
    setError('Google no pudo autenticarte. Intenta de nuevo.');
  };

  return (
    <div className="login-page">
      <motion.div
        className="login-left"
        initial={{ x: -200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="login-logo">Escuela Online</div>
        <img src={illustration} alt="Ilustración registro" className="login-illu" />
      </motion.div>

      <motion.div
        className="login-right"
        initial={{ x: 200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="login-title">Crea tu cuenta</h2>
        {error && <div className="login-error">{error}</div>}

        <form onSubmit={submit} className="login-form">
          <div className="field-group">
            <input
              type="text"
              placeholder="Nombre"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <input
              type="text"
              placeholder="Apellido"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              required
            />
          </div>
          <div className="field-group">
            <input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="field-group password-group">
            <input
              type={showPwd ? 'text' : 'password'}
              placeholder="Contraseña"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="toggle-pwd"
              onClick={() => setShowPwd(!showPwd)}
              aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>
          <div className="field-group password-group">
            <input
              type={showPwd ? 'text' : 'password'}
              placeholder="Confirmar contraseña"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn login-btn" disabled={loading || googleLoading}>
            {loading ? 'Creando cuenta…' : 'Registrarse'}
          </button>
        </form>

        {/* Divider */}
        <div style={{ display: 'grid', placeItems: 'center', margin: '14px 0', opacity: 0.7 }}>
          <span>o</span>
        </div>

        {/* Botón de Google (provider ya viene desde App.jsx) */}
        <div style={{ display: 'grid', placeItems: 'center' }}>
          <GoogleLogin onSuccess={onGoogleSuccess} onError={onGoogleError} useOneTap={false} />
          {googleLoading && (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
              Conectando con Google…
            </div>
          )}
        </div>

        <div className="login-links">
          <a href="/login">¿Ya tienes una cuenta? Inicia sesión</a>
        </div>
      </motion.div>
    </div>
  );
}
